<template>
  <div class="reportWriter">
    <v-tabs align-with-title v-model="currentTab">
      <v-tab key="tab1"> Report </v-tab>
      <v-tab key="tab2"> Details </v-tab>
      <v-tab key="tab3"> Filters </v-tab>
    </v-tabs>

    <v-tabs-items v-model="currentTab" class="pt-1">
      <!-- TAB: REPORT -->
      <v-tab-item key="tab1">
        <v-row>
          <v-col cols="4" class="colWidth">
            <v-text-field
              :readonly="newReport"
              label="Report Name"
              v-model="report.Name"
              dense
              outlined
            ></v-text-field>
            <v-textarea
              label="Description"
              v-model="report.Description"
              outlined
              dense
            ></v-textarea>
            <v-text-field
              label="Report Type"
              v-model="report.ReportType"
              outlined
              dense
            ></v-text-field>
            <v-select
              label="Calculation Input"
              v-model="report.InputForm"
              :items="inputForms"
              item-value="ShortName"
              item-text="ShortName"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>
      </v-tab-item>
      <!-- TAB END: REPORT -->

      <!-- TAB: DETAILS -->
      <v-tab-item>
        <div>
          <div class="btnContainer mb-2">
            <v-btn class="mr-1" text @click="addFields" color="primary">Add Fields</v-btn>
            <v-btn text @click="addCalculation" color="primary">Add Calculation</v-btn>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteSelectedFields" color="error"> Delete Fields </v-btn>
            <v-btn class="mx-2" fab x-small>
              <v-icon @click="moveUp()"> mdi-arrow-up </v-icon>
            </v-btn>
            <v-btn class="mx-2" fab x-small>
              <v-icon @click="moveDown()"> mdi-arrow-down </v-icon>
            </v-btn>
          </div>
          <v-data-table
            v-model="selectedReportFields"
            :headers="reportHeaders"
            :items="report.Details"
            item-key="DetailId"
            class="mb-8"
            :single-select="false"
            :show-select="true"
            disable-pagination
            hide-default-footer
            dense
          >
            <template v-slot:[`item.Output`]="{ item }">
              <v-simple-checkbox v-model="item.Output" disabled></v-simple-checkbox>
            </template>
            <template v-slot:[`item.Grouping`]="{ item }">
              <v-simple-checkbox v-model="item.Grouping" disabled></v-simple-checkbox>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon @click="editItem(item)"> mdi-pencil </v-icon>
            </template>
          </v-data-table>
        </div>

        <!-- DIALOG: EDIT DETAILS FIELD -->
        <v-dialog v-model="editingField" width="500">
          <v-card>
            <v-card-title> Edit Field </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="editedItem.FieldName"
                label="Field Name"
                readonly
              ></v-text-field>
              <v-text-field v-model="editedItem.Heading" label="Alias"></v-text-field>
              <v-text-field v-model="editedItem.SortOrder" label="Sort Order"></v-text-field>
              <v-text-field v-model="editedItem.Format" label="Format"></v-text-field>
              <v-checkbox v-model="editedItem.Output" label="Output"></v-checkbox>
              <v-checkbox v-model="editedItem.Grouping" label="Grouping"></v-checkbox>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" outlined @click="closeEdit">Cancel</v-btn>
              <v-btn color="primary" depressed @click="saveEdit">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- DIALOG END: EDIT DETAILS FIELD -->

        <!-- DIALOG: ADD DETAILS FIELD -->
        <v-dialog v-model="addingFieldsTable" width="800" persistent scrollable>
          <v-card>
            <v-card-title>Add Field</v-card-title>
            <v-card-text class="pb-0 pt-4">
              <div>
                <v-select
                  v-model="selectedTable"
                  :items="tables"
                  label="Select table"
                  dense
                  @change="selectTable(selectedTable)"
                ></v-select>
                <v-data-table
                  v-model="selectedFields"
                  :headers="fieldHeaders"
                  :items="fieldList"
                  item-key="FieldName"
                  class="mb-8"
                  :single-select="false"
                  :show-select="true"
                  disable-pagination
                  hide-default-footer
                  dense
                >
                </v-data-table>
              </div>
            </v-card-text>
            <v-card-actions class="pt-8">
              <v-spacer></v-spacer>
              <v-btn color="primary" outlined @click="cancelAddFields"> Cancel </v-btn>
              <v-btn color="primary" depressed @click="addSelectedFields"> Ok </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- DIALOG END: ADD DETAILS FIELD -->
      </v-tab-item>
      <!-- TAB END: DETAILS -->

      <!-- TAB: FILTERS -->
      <v-tab-item>
        <div class="btnContainer mb-2">
          <v-btn class="mr-4" text @click="addFilter" color="primary">Add Filter</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="onDeleteFilters" color="error">Delete Filters</v-btn>
        </div>
        <v-data-table
          v-model="selectedFilters"
          :items="report.Filters"
          :headers="filterHeaders"
          disable-pagination
          :hide-default-footer="true"
          item-key="FilterId"
          :single-select="false"
          :show-select="true"
          return-object
          dense
        >
          <template v-slot:[`item.OuterJoin`]="{ item }">
            <v-simple-checkbox v-model="item.OuterJoin" disabled></v-simple-checkbox>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon class="mr-2" @click="editFilter(item)"> mdi-pencil </v-icon>
            <!-- <v-icon small @click="deleteFilter(item)"> mdi-delete </v-icon> -->
          </template>
        </v-data-table>

        <!-- DIALOG: EDIT FILTER -->
        <v-dialog v-model="editingFilter" width="500">
          <v-form ref="filterForm">
            <v-card>
              <v-card-title>{{
                editedFilter.FilterId ? 'Edit Filter' : 'Add Filter'
              }}</v-card-title>
              <v-card-text>
                <v-select :items="AndOr" label="AND/OR" v-model="editedFilter.AndOr"></v-select>
                <v-text-field label="Bracket" v-model="editedFilter.LeftBracket"></v-text-field>
                <v-select
                  :items="filterFields"
                  label="Field"
                  v-model="editedFilter.FieldName"
                  :rules="[rules.required]"
                ></v-select>
                <v-select :items="conditions" label="Condition" v-model="editedFilter.Condition">
                </v-select>
                <v-select
                  :items="filterTypes"
                  label="Filter Type"
                  v-model="editedFilter.FilterType"
                ></v-select>
                <v-text-field label="Filter" v-model="editedFilter.Filter"></v-text-field>
                <v-text-field label="Prompt" v-model="editedFilter.Prompt"></v-text-field>
                <v-text-field label="Bracket" v-model="editedFilter.RightBracket"></v-text-field>
                <v-checkbox label="Outer Join" v-model="editedFilter.OuterJoin"></v-checkbox>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined @click="closeFilter">Cancel</v-btn>
                <v-btn color="primary" depressed @click="saveFilter">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
        <!-- DIALOG END: EDIT FILTER -->
      </v-tab-item>
      <!-- TAB END: FILTERS -->
    </v-tabs-items>

    <v-footer padless fixed outlined class="pl-5 pr-5 pt-3 pb-3" color="white" elevation="3">
      <div class="btnContainer">
        <v-btn class="mr-4" outlined @click="backToReportList" color="primary">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn class="mr-4" outlined @click="saveReport" color="primary">Save Report</v-btn>
        <v-btn depressed @click="saveReportAs" color="primary">Save As</v-btn>
      </div>
    </v-footer>

    <!-- IMPORTED DIALOGS -->
    <MultiCalculationSelection ref="calculationBox" :levelId="currentPlanId" @select="onSelect" />
    <Dialog ref="confirmationDialog" v-model="dialog"></Dialog>
  </div>
</template>

<script>
import MultiCalculationSelection from '@/views/Rules/CalculationForms/Popups/MultiCalculationSelection.vue';
import Dialog from '@/components/layout/Dialog.vue';

export default {
  components: { MultiCalculationSelection, Dialog },
  name: 'ReportWriter',
  props: { reportId: { type: Number, required: true } },

  mounted: function () {
    this.getTables();
    this.getReport();
    this.getInputForms();
  },
  data: () => ({
    // oldAddingFields: false,
    selectedTable: null,
    loadingFields: false,
    dialog: false,
    newReport: false,
    editingField: false,
    editingFilter: false,
    editedFilter: {},
    editedItem: {},
    selectedFilter: {},
    addingFields: false,
    addingFieldsTable: false,
    currentTab: 'tab1',
    filterCount: 1,
    detailCount: 1,
    report: {
      Details: [],
      Filters: [],
    },
    inputForms: [],
    tables: [],
    fieldList: [],
    filterList: [],
    filterFields: [],
    selectedField: {},
    selectedFields: [],
    selectedReportFields: [],
    reportFields: [],
    reportHeaders: [
      { text: 'FieldName', value: 'FieldName' },
      { text: 'Tablename', value: 'Tablename' },
      { text: 'Alias', value: 'Heading' },
      { text: 'Order', value: 'SortOrder' },
      { text: 'Output', value: 'Output' },
      { text: 'Group', value: 'Grouping' },
      { text: 'Format', value: 'Format' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
    ],
    fieldHeaders: [
      { text: 'FieldName', value: 'FieldName' },
      { text: 'Alias', value: 'Alias' },
    ],
    filterHeaders: [
      { text: 'And/Or', value: 'AndOr' },
      { text: 'P1', value: 'LeftBracket' },
      { text: 'FieldName', value: 'FieldName' },
      { text: 'Condition', value: 'Condition' },
      { text: 'Filter Type', value: 'FilterType' },
      { text: 'Filter', value: 'Filter' },
      { text: 'Prompt', value: 'Prompt' },
      { text: 'P2', value: 'RightBracket' },
      { text: 'Outer Join', value: 'OuterJoin' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
    ],
    conditions: ['=', '<', '>', '<=', '>=', '<>', 'like', 'IsNull'],
    filterTypes: ['Field', 'Literal', 'Parameter', 'LookupList'],
    AndOr: ['', 'AND', 'OR'],
    tableIndex: 0,
    reportName: '',
    reportDescription: '',
    rules: {
      required: (value) => !!value || 'Required.',
    },
    selectedFilters: [],
    newDetailId: 0,
  }),

  methods: {
    moveUp() {
      if (this.selectedReportFields.length == 1) {
        var item = this.selectedReportFields[0];
        var index = this.report.Details.findIndex((f) => f.DetailId == item.DetailId);
        if (index > 0) {
          this.report.Details.splice(index, 1);
          this.report.Details.splice(index - 1, 0, item);
          console.log(index);
        }
      }
    },
    moveDown() {
      if (this.selectedReportFields.length == 1) {
        var item = this.selectedReportFields[0];
        var index = this.report.Details.findIndex((f) => f.DetailId == item.DetailId);
        if (index < this.report.Details.length - 1) {
          this.report.Details.splice(index, 1);
          this.report.Details.splice(index + 1, 0, item);
          console.log(index);
        }
      }
    },
    addFields() {
      this.addingFieldsTable = true;
    },
    addCalculation() {
      this.$refs.calculationBox.open();
    },
    onSelect(items) {
      items.forEach((item) => {
        var table = item.toUpperCase().includes('INPUT') ? 'Input' : 'Calculation';
        table = item.toUpperCase().includes('FPS.') ? 'SalaryArray' : table;
        if (table === 'SalaryArray') {
          item = item.replace('FPS.', '');
        }
        var field = {
          FieldName: item,
          Tablename: table,
          Heading: table,
          DetailId: this.newDetailId--,
          FieldOrder: this.report.Details.length + 1,
          Filter: '',
          Format: '',
          Grouping: false,
          IsCalculation: false,
          Or1: null,
          Or2: null,
          Output: true,
          ReportId: this.report.ReportId,
          SortOrder: 0,
          Visible: 'Y',
        };
        this.report.Details.push(field);
      });
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.selectedField = item;
      this.editingField = true;
    },
    editFilter(item) {
      this.editedFilter = Object.assign({}, item);
      this.selectedFilter = item;
      this.editingFilter = true;
      this.filterFields = [];
      this.report.Details.forEach((item) => {
        this.filterFields.push(item.Tablename + '.' + item.FieldName);
      });
    },
    deleteItem(item) {
      console.log(item);
    },
    async deleteFilter(item) {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete these fields ?'
        );
        this.report.Filters = this.report.Filters.filter((f) => f != item);
      } catch (err) {
        console.error(err);
      }
    },
    async onDeleteFilters() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete these fields ?'
        );
        this.report.Filters = this.report.Filters.filter(
          (item) => this.selectedFilters.indexOf(item) < 0
        );
        this.selectedFilters = [];
      } catch (err) {
        console.error(err);
      }
    },
    addFilter() {
      if (this.filterList == null) {
        this.filterList = [];
      }
      this.filterList.push({});
      this.editingFilter = true;
      this.editedFilter = { FilterId: 0 };
      this.filterFields = [];
      this.report.Details.forEach((item) => {
        this.filterFields.push(item.Tablename + '.' + item.FieldName);
      });
    },

    saveFilter() {
      console.log('save filter', this.editedFilter.FilterId);
      if (this.$refs.filterForm.validate()) {
        if (this.editedFilter.FilterId == 0) {
          this.selectedFilter = { FilterId: 1 };
          this.selectedFilter.FilterId = this.filterCount;
          this.filterCount++;
          if (this.report.Filters == null) {
            this.report.Filters = [];
            this.report.Filters.push(this.selectedFilter);
          } else {
            this.report.Filters.push(this.selectedFilter);
            //this.report.Filters.splice(this.report.Filters.length-1,1,this.selectedFilter);
            console.log(this.report.Filters);
          }
        }
        this.selectedFilter.AndOr = this.editedFilter.AndOr;
        this.selectedFilter.IsOr = this.editedFilter.AndOr == 'OR';
        this.selectedFilter.FieldName = this.editedFilter.FieldName;
        this.selectedFilter.Condition = this.editedFilter.Condition;
        this.selectedFilter.FilterType = this.editedFilter.FilterType;
        this.selectedFilter.Filter = this.editedFilter.Filter;
        this.selectedFilter.LeftBracket = this.editedFilter.LeftBracket;
        this.selectedFilter.RightBracket = this.editedFilter.RightBracket;
        this.selectedFilter.OuterJoin = this.editedFilter.OuterJoin;
        this.selectedFilter.Prompt = this.editedFilter.Prompt;
        this.editingFilter = false;
      }
    },

    backToReportList() {
      // this.$router.push({ name: 'ReportsList' });
      this.$emit('backToList', false);
    },

    async deleteSelectedFields() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete these fields ?'
        );
        this.report.Details = this.report.Details.filter(
          (item) => this.selectedReportFields.indexOf(item) < 0
        );
        this.selectedReportFields = [];
      } catch (err) {
        console.error(err);
      }
    },
    addSelectedFields() {
      console.log('add fields');
      this.selectedFields.forEach((item) => {
        if (
          this.report.Details.find(
            (d) => d.FieldName === item.FieldName && d.Tablename === item.TableName
          ) == null
        ) {
          const field = {
            DetailId: this.detailCount++,
            FieldName: item.FieldName,
            Heading: item.Alias,
            FieldOrder: this.report.Details.length + 1,
            Filter: '',
            Format: '',
            Grouping: false,
            IsCalculation: false,
            Or1: null,
            Or2: null,
            Output: true,
            ReportId: this.report.ReportId,
            SortOrder: 0,
            Tablename: item.TableName,
            Visible: 'Y',
          };
          this.report.Details.push(field);
          console.log(field);
        }
      });

      this.addingFieldsTable = false;
      this.selectedFields = [];
      this.selectedTable = null;
      this.fieldList = [];
      console.log('added');
    },
    cancelAddFields() {
      this.addingFieldsTable = false;
      this.selectedFields = [];
      this.selectedTable = null;
    },
    async selectTable(item) {
      this.loadingFields = true;
      try {
        let response = await this.$api.get('/DataDictionary/GetFormFieldList?formName=' + item);
        this.fieldList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingFields = false;
      }
    },
    async getReport() {
      try {
        // var reportId = this.$route.query.id;
        if (this.reportId == null || this.reportId == 0) {
          console.log('add');
          this.report.Details = [];
          this.report.Filters = [];
          console.log('new report');
        } else {
          let response = await this.$api.get('/report/GetReportById?Id=' + this.reportId);

          this.report = response.data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getTables() {
      try {
        let response = await this.$api.get('/DataDictionary/GetFormList');
        this.tables = response.data;
      } catch (err) {
        console.error(err);
      }
    },
    async getInputForms() {
      try {
        let response = await this.$api.get(
          '/Calculation/GetCalculationItems?planId=0&groupId=0&calcType=Forms'
        );
        this.inputForms = response.data;
        this.inputForms.splice(0, 0, { ShortName: '' });
      } catch (err) {
        console.error(err);
      }
    },
    closeEdit() {
      this.editingField = false;
    },
    closeFilter() {
      this.editingFilter = false;
    },
    saveEdit() {
      this.editingField = false;
      this.selectedField.Heading = this.editedItem.Heading;
      this.selectedField.Format = this.editedItem.Format;
      this.selectedField.SortOrder = this.editedItem.SortOrder;
      this.selectedField.Grouping = this.editedItem.Grouping;
      this.selectedField.Output = this.editedItem.Output;
      this.selectedField.Visible = this.editedItem.Output ? 'Y' : 'N';
    },
    saveReportAs() {
      var that = this;
      this.$refs.confirmationDialog
        .open('Save As', 'Please enter a new name', { textField: 'Report Name' })
        .then(function (name) {
          that.report.ReportId = 0;
          that.report.Name = name;
          that.saveReport();
        });
    },
    async saveReport() {
      try {
        let response = await this.$api.post('/report/savereport', this.report);
        if (this.report.ReportId == 0) this.report.ReportId = response.data.Value;
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Report was saved',
        });
        this.$emit('backToList', true);
      } catch (err) {
        console.error(err);
      }
    },
  },
  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
  },
};
</script>

<style lang="scss">
.reportWriter {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;

  .v-tabs {
    margin-bottom: 10px;

    .v-tabs-slider {
      width: 0px;
    }

    .v-tab {
      border: 1px solid;
      border-radius: 30px;
      color: #4a1cd4 !important;
      height: 35px;
      margin-right: 10px;

      &.v-tab--active {
        color: white !important;
        background-color: #4a1cd4;
      }
    }
  }
  .colWidth {
    max-width: 500px;
  }
  .btnContainer {
    width: 100%;
    display: flex;
  }
  .buttons {
    margin-left: 20px;
    margin-top: 20px;
  }
  .buttons button {
    margin-right: 20px;
  }
  .selected {
    background-color: #e3f2fd !important;
  }
  .v-tabs-items {
    min-height: 500px;
  }
  .v-list {
    max-height: 700px;
    overflow-y: auto;
  }
}
</style>
